import { formatLifecycleCell } from "./formatLifecycleCell";

export type LifecycleApiRow = {
  kind: "ENQUIRY" | "CONFIRMED_ORDER";
  orderNo: string;
  orderStatus: string;
  customerName?: string;
  orderHandlerName?: string;
  initiatorName?: string;
  /** Enquiry ENQUIRY PLACED (enquiry.orderDate ?? createdAt) — first CT stage */
  enquiryPlacedAt?: string | null;
  /** Confirmed orders: same as Orca ORDER PLACED (order.createdAt) */
  orderDate?: string | null;
  createdAt?: string | null;
  ackAt?: string | null;
  partConversionStartAt?: string | null;
  partConversionEndAt?: string | null;
  customerHoldStartAt?: string | null;
  customerHoldEndAt?: string | null;
  allocatedAt?: string | null;
  supplierAckAt?: string | null;
  supplierPickedAt?: string | null;
  supplierInReviewAt?: string | null;
  supplierReadyAt?: string | null;
  supplierCancelledAt?: string | null;
  supplierRevokedAt?: string | null;
  cboStartAt?: string | null;
  cboEndAt?: string | null;
  readyToDispatchAt?: string | null;
  dispatchedAt?: string | null;
  dispatchRiderName?: string | null;
  shippedAt?: string | null;
  deliveredAt?: string | null;
  deliveredByName?: string | null;
  cancelledAt?: string | null;
};

export type PartConfirmationApiRow = {
  supplierId: string;
  supplierName: string;
  status: string;
  sentAt: string | null;
  expectedAt: string | null;
  receivedAt: string | null;
};

export type SupplierAllocationApiRow = {
  supplierId: string;
  supplierName: string;
  /** PROCESS | STOCK_OUT | STOCK_IN */
  actionType?: string | null;
  status: string;
  isActive?: boolean | null;
  allocationTime?: string | null;
  stateTime?: string | null;
  supplierRespondedTime?: string | null;
  /** Legacy: current-status time (stateTime ?? allocationTime) */
  at: string | null;
};

export type EstimateApiRow = {
  estimateNo: string;
  status: string;
  createdAt?: string | null;
  estimateSharedAt?: string | null;
  respondedAt?: string | null;
};

export type ShipmentApiRow = {
  shipmentId: string;
  status: string;
  createdAt?: string | null;
  pickedUpAt?: string | null;
  deliveredAt?: string | null;
  cancelledAt?: string | null;
};

export type LifecycleStep = {
  id: string;
  status: string;
  at: string;
  atRaw: string | null;
  person: string;
  diffFromPrevious: string;
  /** Full id shown on hover when status label is shortened. */
  statusTooltip?: string | null;
};

export function toMillis(
  value: string | number | Date | null | undefined,
): number {
  if (value == null || value === "") return 0;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isFinite(ms) ? ms : 0;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : 0;
}

export function formatDurationMs(ms: number): string {
  if (ms <= 0) return "0";
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 && hours === 0) parts.push(`${seconds}s`);
  if (parts.length === 0) return "0";
  return parts.join(" ");
}

/**
 * Diff aligned to second-precision displayed clocks (hh:mm:ss).
 * So :14 → :15 is always "1s", even if the raw gap is only a few hundred ms.
 */
export function formatDiffFromPrevious(
  previousMs: number,
  currentMs: number,
): string {
  if (previousMs <= 0 || currentMs <= 0) return "0";
  const diffSeconds =
    Math.floor(currentMs / 1000) - Math.floor(previousMs / 1000);
  return formatDurationMs(Math.max(0, diffSeconds) * 1000);
}

function personOrDash(name: string | undefined | null): string {
  const trimmed = String(name ?? "").trim();
  return trimmed || "—";
}

function supplierActionLabel(actionType: string | null | undefined): string {
  const t = String(actionType ?? "PROCESS").trim().toUpperCase();
  if (t === "STOCK_OUT") return "STOCK_OUT";
  if (t === "STOCK_IN") return "STOCK_IN";
  return "PROCESS";
}

function supplierStatusLabel(status: string): string {
  switch (String(status || "").toUpperCase()) {
    case "ACK":
      return "Supplier ACK";
    case "PICKED":
      return "Supplier Picked";
    case "IN_REVIEW":
      return "Supplier In Review";
    case "READY":
      return "Supplier Ready";
    case "CANCELLED":
      return "Supplier Cancelled";
    case "REVOKED":
      return "Supplier Revoked";
    case "NEW":
      return "Supplier NEW";
    default:
      return `Supplier ${status || "—"}`;
  }
}

type StepDef = {
  id: string;
  status: string;
  at: string | null | undefined;
  personWhenPresent: string;
  statusTooltip?: string | null;
};

function shortEstimateNo(estimateNo: string): string {
  const s = String(estimateNo ?? "").trim();
  if (!s) return "";
  return `#${s.slice(-4)}`;
}

function shortShipmentId(shipmentId: string): string {
  const s = String(shipmentId ?? "").trim();
  if (!s) return "";
  return `#${s.slice(-4)}`;
}

/**
 * Fixed skeleton + dynamic part-confirmation / estimate / shipment events (all of them).
 * Diff is vs the previous *timed* step.
 */
export function buildLifecycleSteps(
  row: LifecycleApiRow,
  partConfirmations?: PartConfirmationApiRow[],
  supplierAllocations?: SupplierAllocationApiRow[],
  estimates?: EstimateApiRow[],
  shipments?: ShipmentApiRow[],
): LifecycleStep[] {
  const handler = personOrDash(row.orderHandlerName);
  const initiator = personOrDash(row.initiatorName);

  // Actor is not stored on estimate / part-confirmation / dispatch docs — never invent PERSON.
  const unknownActor = "—";

  const partConfDefs: StepDef[] = [];
  const pcs = [...(partConfirmations ?? [])].sort(
    (a, b) => toMillis(a.sentAt) - toMillis(b.sentAt),
  );
  if (pcs.length === 0) {
    partConfDefs.push(
      {
        id: "part-conf-sent",
        status: "Part confirmation sent",
        at: null,
        personWhenPresent: unknownActor,
      },
      {
        id: "part-conf-received",
        status: "Part confirmation received",
        at: null,
        personWhenPresent: unknownActor,
      },
    );
  } else {
    pcs.forEach((pc, index) => {
      const label = personOrDash(pc.supplierName);
      const fullId = String(pc.supplierId || "").trim() || label;
      partConfDefs.push({
        id: `part-conf-sent-${pc.supplierId || index}`,
        status: `Part confirmation sent (${label})`,
        at: pc.sentAt,
        personWhenPresent: unknownActor,
        statusTooltip: fullId,
      });
      partConfDefs.push({
        id: `part-conf-received-${pc.supplierId || index}`,
        status: `Part confirmation received (${label})`,
        at: pc.receivedAt,
        personWhenPresent: unknownActor,
        statusTooltip: fullId,
      });
    });
  }

  const estimateDefs: StepDef[] = [];
  const estimateList = [...(estimates ?? [])].sort(
    (a, b) => toMillis(a.createdAt) - toMillis(b.createdAt),
  );
  if (estimateList.length === 0) {
    estimateDefs.push(
      {
        id: "estimate-created",
        status: "Estimate created",
        at: null,
        personWhenPresent: unknownActor,
      },
      {
        id: "estimate-shared",
        status: "Estimate shared",
        at: null,
        personWhenPresent: unknownActor,
      },
      {
        id: "estimate-responded",
        status: "Estimate responded",
        at: null,
        personWhenPresent: unknownActor,
      },
    );
  } else {
    estimateList.forEach((est, index) => {
      const fullNo = String(est.estimateNo || "").trim();
      const tag = shortEstimateNo(fullNo || String(index));
      const tooltip = fullNo || null;
      estimateDefs.push({
        id: `estimate-created-${est.estimateNo || index}`,
        status: `Estimate created ${tag}`,
        at: est.createdAt,
        personWhenPresent: unknownActor,
        statusTooltip: tooltip,
      });
      // Only emit shared/responded when those stamps exist (DRAFT stays as created only).
      if (est.estimateSharedAt) {
        estimateDefs.push({
          id: `estimate-shared-${est.estimateNo || index}`,
          status: `Estimate shared ${tag}`,
          at: est.estimateSharedAt,
          personWhenPresent: unknownActor,
          statusTooltip: tooltip,
        });
      }
      if (est.respondedAt) {
        estimateDefs.push({
          id: `estimate-responded-${est.estimateNo || index}`,
          status: `Estimate responded ${tag}`,
          at: est.respondedAt,
          personWhenPresent: unknownActor,
          statusTooltip: tooltip,
        });
      }
    });
  }

  const shipmentDefs: StepDef[] = [];
  const shipmentList = [...(shipments ?? [])].sort(
    (a, b) => toMillis(a.createdAt) - toMillis(b.createdAt),
  );
  if (shipmentList.length === 0) {
    shipmentDefs.push({
      id: "shipment-created",
      status: "Shipment created",
      at: null,
      personWhenPresent: unknownActor,
    });
  } else {
    shipmentList.forEach((ship, index) => {
      const fullId = String(ship.shipmentId || "").trim();
      const tag = shortShipmentId(fullId || String(index));
      const tooltip = fullId
        ? `${fullId}${ship.status ? ` · ${ship.status}` : ""}`
        : ship.status || null;
      const key = fullId || String(index);

      shipmentDefs.push({
        id: `shipment-created-${key}`,
        status: `Shipment created ${tag}`,
        at: ship.createdAt,
        personWhenPresent: unknownActor,
        statusTooltip: tooltip,
      });
      if (ship.pickedUpAt) {
        shipmentDefs.push({
          id: `shipment-picked-${key}`,
          status: `Shipment picked up ${tag}`,
          at: ship.pickedUpAt,
          personWhenPresent: unknownActor,
          statusTooltip: tooltip,
        });
      }
      if (ship.deliveredAt) {
        shipmentDefs.push({
          id: `shipment-delivered-${key}`,
          status: `Shipment delivered ${tag}`,
          at: ship.deliveredAt,
          personWhenPresent: unknownActor,
          statusTooltip: tooltip,
        });
      }
      if (ship.cancelledAt) {
        shipmentDefs.push({
          id: `shipment-cancelled-${key}`,
          status: `Shipment cancelled ${tag}`,
          at: ship.cancelledAt,
          personWhenPresent: unknownActor,
          statusTooltip: tooltip,
        });
      }
    });
  }

  // One stage group per allocatedTo (PROCESS / STOCK_OUT / STOCK_IN).
  const supplierDefs: StepDef[] = [];
  const usedAllocationKeys = new Set<string>();
  const uniqueAllocationKey = (base: string): string => {
    if (!usedAllocationKeys.has(base)) {
      usedAllocationKeys.add(base);
      return base;
    }
    let n = 2;
    while (usedAllocationKeys.has(`${base}-${n}`)) n += 1;
    const unique = `${base}-${n}`;
    usedAllocationKeys.add(unique);
    return unique;
  };
  const allocations = [...(supplierAllocations ?? [])].sort(
    (a, b) =>
      toMillis(a.allocationTime ?? a.at) - toMillis(b.allocationTime ?? b.at),
  );
  if (allocations.length === 0) {
    supplierDefs.push({
      id: "supplier-allocated",
      status: "Supplier allocated",
      at: null,
      personWhenPresent: unknownActor,
    });
  } else {
    allocations.forEach((alloc, index) => {
      const name = personOrDash(alloc.supplierName);
      const action = supplierActionLabel(alloc.actionType);
      const tag = `${name} · ${action}`;
      // Disambiguate only on collision (same supplierId + action re-allocated).
      const key = uniqueAllocationKey(
        `${alloc.supplierId || index}-${action}`,
      );
      const tooltip = [
        alloc.supplierId,
        action,
        alloc.status,
        alloc.isActive === false ? "inactive" : null,
      ]
        .filter(Boolean)
        .join(" · ");
      const person = name;

      supplierDefs.push({
        id: `supplier-allocated-${key}`,
        status: `Supplier allocated (${tag})`,
        at: alloc.allocationTime ?? alloc.at,
        personWhenPresent: person,
        statusTooltip: tooltip,
      });

      const respondedAt = alloc.supplierRespondedTime ?? null;
      const stateAt = alloc.stateTime ?? alloc.at ?? null;
      const status = String(alloc.status || "").toUpperCase();

      // supplierRespondedTime is set once on first IN_REVIEW — emit that stamp.
      if (respondedAt) {
        supplierDefs.push({
          id: `supplier-in-review-${key}`,
          status: `Supplier In Review (${tag})`,
          at: respondedAt,
          personWhenPresent: person,
          statusTooltip: tooltip,
        });
      }

      // Current status (skip NEW; skip IN_REVIEW if already emitted from respondedAt).
      const skipCurrent =
        status === "NEW" ||
        status === "" ||
        (status === "IN_REVIEW" && !!respondedAt);

      if (!skipCurrent && stateAt) {
        // Avoid duplicate timestamp when READY/etc. but stateTime equals responded.
        if (
          respondedAt &&
          toMillis(stateAt) === toMillis(respondedAt) &&
          status === "IN_REVIEW"
        ) {
          // already emitted
        } else {
          supplierDefs.push({
            id: `supplier-status-${status.toLowerCase()}-${key}`,
            status: `${supplierStatusLabel(status)} (${tag})`,
            at: stateAt,
            personWhenPresent: person,
            statusTooltip: tooltip,
          });
        }
      }
    });

    // Chronological order so DIFF matches real elapsed time across suppliers.
    supplierDefs.sort((a, b) => {
      const aMs = toMillis(a.at);
      const bMs = toMillis(b.at);
      if (aMs === 0 && bMs === 0) return 0;
      if (aMs === 0) return 1;
      if (bMs === 0) return -1;
      return aMs - bMs;
    });
  }

  const enquiryPlacedDef: StepDef = {
    id: "enquiry-placed",
    status: "Enquiry Placement Date",
    // Same source as Orca enquiry header (ENQUIRY PLACED → orderDate ?? createdAt)
    at: row.enquiryPlacedAt,
    personWhenPresent: initiator !== "—" ? initiator : unknownActor,
  };

  const orderPlacedDef: StepDef = {
    id: "order-date",
    status: "Order placed",
    // Same source as Orca confirmed-order header (ORDER PLACED → createdAt)
    at: row.createdAt ?? row.orderDate,
    personWhenPresent: initiator !== "—" ? initiator : "System",
  };

  const preConfirmDefs: StepDef[] = [
    {
      id: "ack",
      status: "ACK",
      at: row.ackAt,
      personWhenPresent: handler,
    },
    {
      id: "pc-start",
      status: "Part conversion start",
      at: row.partConversionStartAt,
      personWhenPresent: handler,
    },
    {
      id: "pc-end",
      status: "Part conversion end",
      at: row.partConversionEndAt,
      personWhenPresent: handler,
    },
    ...partConfDefs,
    ...estimateDefs,
  ];

  // Timeline: Enquiry Placement Date first, then enquiry-phase work, then
  // Order placed (confirmed createdAt) for confirmed-order searches.
  const defs: StepDef[] = [
    enquiryPlacedDef,
    ...(row.kind === "CONFIRMED_ORDER"
      ? [...preConfirmDefs, orderPlacedDef]
      : [orderPlacedDef, ...preConfirmDefs]),
    {
      id: "hold-start",
      status: "Customer hold start",
      at: row.customerHoldStartAt,
      personWhenPresent: handler,
    },
    {
      id: "hold-end",
      status: "Customer hold end",
      at: row.customerHoldEndAt,
      personWhenPresent: handler,
    },
    {
      id: "allocated",
      status: "ALLOCATED",
      at: row.allocatedAt,
      personWhenPresent: handler,
    },
    ...supplierDefs,
    {
      id: "cbo-start",
      status: "CBO start",
      at: row.cboStartAt,
      personWhenPresent: handler,
    },
    {
      id: "cbo-end",
      status: "CBO end",
      at: row.cboEndAt,
      personWhenPresent: handler,
    },
    {
      id: "r2d",
      status: "Ready to dispatch",
      at: row.readyToDispatchAt,
      personWhenPresent: handler,
    },
    {
      id: "dispatched",
      status: "Dispatched",
      at: row.dispatchedAt,
      // Rider is assignee, not who performed dispatch — no actor field stored.
      personWhenPresent: unknownActor,
    },
    ...shipmentDefs,
    {
      id: "delivered",
      status: "Delivered",
      at: row.deliveredAt,
      // deliveryPerson is not a verified action actor — leave empty.
      personWhenPresent: unknownActor,
    },
    {
      id: "cancelled",
      status: "Cancelled",
      at: row.cancelledAt,
      personWhenPresent: handler,
    },
  ];

  let previousMs = 0;
  let hasPreviousTimed = false;

  return defs.map((d) => {
    const formatted = formatLifecycleCell(d.at);
    const hasTime = formatted !== "—";
    const statusTooltip = d.statusTooltip || null;

    if (!hasTime) {
      return {
        id: d.id,
        status: d.status,
        at: "—",
        atRaw: null,
        person: "—",
        diffFromPrevious: "—",
        statusTooltip,
      };
    }

    const atRaw = String(d.at);
    const currentMs = toMillis(atRaw);
    const diffFromPrevious = !hasPreviousTimed
      ? "0"
      : formatDiffFromPrevious(previousMs, currentMs);
    previousMs = currentMs;
    hasPreviousTimed = true;

    return {
      id: d.id,
      status: d.status,
      at: formatted,
      atRaw,
      person: d.personWhenPresent,
      diffFromPrevious,
      statusTooltip,
    };
  });
}
